<template>
  <div>
    <v-card :class="editedItemActionAt || editing ? 'ret-card' : 'ret-card ret-card-pipetask-done'">
      <v-card-title>
        <div v-if="project.id">
          <a @click="$emit('routeToProject', project.id)">{{ project.code }}</a>
        </div>
        <v-menu bottom right :min-width="240">
          <template v-slot:activator="{ on }">
            <v-btn text icon v-on="on">
              <v-icon v-if="editedItem.category === 'note'">notes</v-icon>
              <v-icon v-else-if="editedItem.category === 'meeting'">meeting_room</v-icon>
              <v-icon v-else-if="editedItem.category === 'epic'">flash_on</v-icon>
              <v-icon v-else-if="editedItem.category === 'material'">developer_board</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="setCategory('note')">
              <v-list-item-avatar>
                <v-icon>notes</v-icon>
              </v-list-item-avatar>
              <v-list-item-title>Note</v-list-item-title>
            </v-list-item>
            <v-list-item @click="setCategory('meeting')">
              <v-list-item-avatar>
                <v-icon>meeting_room</v-icon>
              </v-list-item-avatar>
              <v-list-item-title>Meeting</v-list-item-title>
            </v-list-item>
            <v-list-item @click="setCategory('epic')">
              <v-list-item-avatar>
                <v-icon>flash_on</v-icon>
              </v-list-item-avatar>
              <v-list-item-title>Epic</v-list-item-title>
            </v-list-item>
            <v-list-item @click="setCategory('material')">
              <v-list-item-avatar>
                <v-icon>developer_board</v-icon>
              </v-list-item-avatar>
              <v-list-item-title>Material</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <ret-date-picker v-if="!editedItemActionAt || editedItem.category === 'meeting'"
                         v-model="editedItemDatum" :showIcon="false"></ret-date-picker>
      </v-card-title>

      <v-card-text v-if="!editing" class="ret-compact">
        <p class="ret-name" @click="editName">{{ editedItem.name || '[No description]' }}</p>
        <p v-if="editedItem.after" @click="editAfter" class="ret-after">After: {{ editedItem.after }}</p>
        <div v-if="editedItem.minutes" @click="showMinutes" class="ret-link">
          <v-icon>attachment</v-icon>
          <div class="ret-link-label">Minutes</div>
        </div>
      </v-card-text>

      <v-card-text v-else-if="editing">
        <v-textarea ref="name" auto-grow :rows="1" v-model="editedItem.name"></v-textarea>
        <center>
          <v-btn icon @click="editingMore = !editingMore">
            <v-icon>{{ editingMore ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon>
          </v-btn>
        </center>
        <div v-if="editingMore">
          <v-layout row wrap class="ret-form">
            <v-flex xs12>
              <v-textarea ref="after" auto-grow :rows="1" v-model="editedItem.after" prepend-inner-icon="skip_next"
                          label="After"></v-textarea>
            </v-flex>
            <v-flex xs12>
              <v-text-field v-if="editedItem.category === 'meeting'" v-model="editedItem.minutes"
                            prepend-inner-icon="insert_link" label="Minutes"></v-text-field>
            </v-flex>
          </v-layout>
          <v-layout row wrap class="ret-form">
            <v-flex xs12 md4>
              <v-autocomplete
                clearable
                :items="users"
                v-model="editedItem.assigneeUserId"
                item-value="id"
                item-text="displayName"
                prepend-inner-icon="person"
                label="Assignee">
              </v-autocomplete>
            </v-flex>
            <v-flex xs12 md4>
              <v-autocomplete
                clearable
                :items="users"
                v-model="editedItem.reporterUserId"
                item-value="id"
                item-text="displayName"
                prepend-inner-icon="accessibility"
                label="Reporter">
              </v-autocomplete>
            </v-flex>
            <v-flex xs12 md4>
              <v-text-field v-model="editedItem.waitingFor" prepend-inner-icon="timer"
                            label="Waiting for"></v-text-field>
            </v-flex>
          </v-layout>
        </div>
      </v-card-text>

      <v-card-text v-if="pipeTasksInProjects.length > 1">
        <v-chip outlined close v-for="pipeTaskIn of pipeTasksInProjects" :key="pipeTaskIn.id"
                @click="$emit('routeToProject', pipeTaskIn.projectId)"
                @input="unlinkFromProject(pipeTaskIn.id)">
          {{ pipeTaskIn.project.code }}
        </v-chip>
      </v-card-text>

      <v-card-actions v-if="!editing">
        <div v-if="editedItemActionAt" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
          <ret-date-picker v-model="editedItemActionAt"></ret-date-picker>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn icon text color="grey" v-on="on">
                <v-icon v-if="editedItem.assigneeUserId">person</v-icon>
                <v-icon v-else>person_outline</v-icon>
              </v-btn>
            </template>
            <span>{{ (assigneeUser && assigneeUser.displayName) ? assigneeUser.displayName : 'Unassigned' }}</span>
          </v-tooltip>
          <span>{{ assigneeUser.code }}</span>

          <span v-if="editedItem.waitingFor">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon text color="grey" v-on="on">
                  <v-icon>timer</v-icon>
                </v-btn>
              </template>
              <span>{{ 'Waiting for' }}</span>
            </v-tooltip>
            <span>{{ editedItem.waitingFor }}</span>
          </span>
        </div>
      </v-card-actions>

      <v-card-actions v-else-if=editing>
        <v-btn @click="cancel">Cancel</v-btn>
        <v-btn color="primary" @click="saveAndFinishEditing">Save</v-btn>
        <v-spacer></v-spacer>
        <v-btn v-if="editedItem.actionAt" text icon @click="setDone">
          <v-icon>done</v-icon>
        </v-btn>
        <v-btn v-else text icon @click="setActionable">
          <v-icon>done_all</v-icon>
        </v-btn>
        <v-menu bottom right :min-width="240">
          <template v-slot:activator="{ on }">
            <v-btn text icon v-on="on">
              <v-icon>link</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-for="project of projects" :key="project.id" @click="linkToProject(project.id)">
              <v-list-item-title>{{ project.code }} {{ project.name }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn icon text color="warn" @click="destroy">
          <v-icon>delete</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<style scoped>
  .ret-card-pipetask-done {
    color: rgba(0, 0, 0, 0.54);
  }

  .theme--light.v-card.ret-card-pipetask-done {
    background-color: rgba(255, 255, 255, 0.0);
    box-shadow: none;
    border: 1px solid rgba(0, 0, 0, 0.15);
  }

  .ret-card-pipetask-done a {
    color: rgba(0, 0, 0, 0.54);
  }

  div.ret-link {
    cursor: pointer;
    display: inline-block;
  }

  div.ret-link i.v-icon {
    vertical-align: middle;
  }

  div.ret-link div.ret-link-label {
    display: inline-flex;
    vertical-align: middle;
    padding: 0px 10px;
    color: rgba(0, 0, 0, 0.54);
  }

  .ret-pipe-icon {
    margin: 8px 14px;
  }

  p.ret-name {
    white-space: pre-wrap;
    margin: 0px;
  }

  p.ret-after {
    white-space: pre-wrap;
    margin: 0px;
    color: rgba(0, 0, 0, 0.54);
  }

  div.card__text.ret-compact {
    padding-top: 0px;
  }

  .ret-pipe-task-div {
    max-width: 1200px;
    margin: 0px auto;
    padding: 16px;
    padding-top: 8px;
  }

  .ret-pipe-task-div p {
    white-space: pre-wrap;
    margin: 0px;
    padding-top: 8px;
    color: rgba(0, 0, 0, 0.54);
  }

  span.chip {
    margin-left: 8px;
    margin-right: 8px;
  }

</style>

<script>
import displayHelpers from '../../helpers/display-helpers.es6'
import RetDatePicker from '../../shared/components/ret-date-picker.vue'
import Vue from 'vue'
import moment from 'moment'

export default {
  name: 'pipe-task',
  props: {
    item: Object,
    users: Array,
    projects: Array
  },
  mixins: [displayHelpers],
  data () {
    return {
      editing: false,
      editingMore: false,
      editedItem: { ...this.item }
    }
  },
  computed: {
    edited () {
      return Object.keys(this.item).reduce((result, itemKey) => {
        if (this.item[itemKey] !== this.editedItem[itemKey]) {
          return true
        }
        return result
      }, false)
    },
    editedItemActionAt: {
      get () {
        return this.editedItem.actionAt ? moment(this.editedItem.actionAt) : undefined
      },
      set (val) {
        this.editedItem.actionAt = val
        this.save()
      }
    },
    editedItemDatum: {
      get () {
        return this.editedItem.datum ? moment(this.editedItem.datum) : undefined
      },
      set (val) {
        this.editedItem.datum = val
        this.save()
      }
    },
    project () {
      return this.item.pipeTasksInProjects[0] ? this.item.pipeTasksInProjects[0].project : {}
    },
    pipeTasksInProjects () {
      return this.editedItem.pipeTasksInProjects || []
    },
    assigneeUser () {
      const user = this.users.find(user => user.id === this.editedItem.assigneeUserId)
      return (user && user.id) ? user : {}
    }
  },
  methods: {
    editName () {
      this.editing = true
      Vue.nextTick(() => {
        const { name } = this.$refs
        name.focus()
      })
    },
    editAfter () {
      this.editing = true
      this.editingMore = true
      Vue.nextTick(() => {
        const { after } = this.$refs
        after.focus()
      })
    },
    showMinutes () {
      // window.location.href = this.editedItem.minutes
      window.open(this.editedItem.minutes, '_blank')
    },
    saveAndFinishEditing () {
      this.editing = false
      this.editingMore = false
      this.save()
    },
    save () {
      const pipeTask = this.editedItem
      // this.$emit('updateItem', Object.assign({}, this.editedItem, {projectId: this.project.id}))
      this.$emit('updateItem', pipeTask)
    },
    cancel () {
      this.editedItem = { ...this.item }
      this.editing = false
      this.editingMore = false
    },
    setDone () {
      this.editedItem.actionAt = null
      this.editedItem.waitingFor = null
      this.saveAndFinishEditing()
    },
    setActionable () {
      this.editedItem.actionAt = moment()
      this.save()
    },
    setCategory (category) {
      this.editedItem.category = category
      this.save()
    },
    destroy () {
      this.$emit('destroyItem', this.editedItem)
    },
    linkToProject (projectId) {
      const pipeTaskInProject = {
        projectId,
        pipeTaskId: this.editedItem.id
      }
      this.$emit('updateLinkedItem', pipeTaskInProject)
    },
    unlinkFromProject (pipeTaskInProjectId) {
      this.$emit('destroyLinkedItem', pipeTaskInProjectId)
    }
  },
  watch: {
    item: {
      handler (val) {
        this.editedItem = { ...this.item }
      },
      deep: true
    }
  },
  components: {
    RetDatePicker
  }
}
</script>
